import { useState, useEffect, useRef, useCallback } from 'react';
import Hls from 'hls.js';
import { HeroVideo } from '../types';
import { VideoItem } from '../components/VideoCarousel/VideoCarouselModal';
import { searchVideos, getVideoDuration, getVideoUrl, isHlsUrl } from '../utils/video-utils';
import { useFotbollPlayTeamChannels } from './useFotbollPlayTeamChannels';

interface UseHeroVideoOptions {
  teamAbbrv: string;
}

interface UseHeroVideoReturn {
  heroVideo: HeroVideo | null;
  heroVideoSearchQuery: string;
  heroVideoSearchResults: VideoItem[];
  isSearchingHeroVideo: boolean;
  isPreviewPlaying: boolean;
  actualVideoDuration: number | null;
  videoRef: React.RefObject<HTMLVideoElement>;
  videoUrl: string;
  setHeroVideoSearchQuery: (query: string) => void;
  handleSelectHeroVideo: (video: VideoItem) => void;
  handleRemoveHeroVideo: () => void;
  handleLoopRangeChange: (event: Event, newValue: number | number[]) => void;
  handleVideoTimeUpdate: () => void;
  handleLoadedMetadata: () => void;
  handlePlayPreview: () => void;
  handlePausePreview: () => void;
  getEffectiveDuration: () => number;
  setHeroVideo: React.Dispatch<React.SetStateAction<HeroVideo | null>>;
}

export function useHeroVideo({ teamAbbrv }: UseHeroVideoOptions): UseHeroVideoReturn {
  const [heroVideo, setHeroVideo] = useState<HeroVideo | null>(null);
  const [heroVideoSearchQuery, setHeroVideoSearchQuery] = useState('');
  const [heroVideoSearchResults, setHeroVideoSearchResults] = useState<VideoItem[]>([]);
  const [isSearchingHeroVideo, setIsSearchingHeroVideo] = useState(false);
  const [isPreviewPlaying, setIsPreviewPlaying] = useState(false);
  const [actualVideoDuration, setActualVideoDuration] = useState<number | null>(null);
  const heroVideoDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const hlsRef = useRef<Hls | null>(null);
  const { channels } = useFotbollPlayTeamChannels(teamAbbrv);

  // Debounced video search for hero
  useEffect(() => {
    if (!heroVideoSearchQuery.trim()) {
      setHeroVideoSearchResults([]);
      return;
    }

    if (channels.length === 0) {
      setHeroVideoSearchResults([]);
      return;
    }

    if (heroVideoDebounceRef.current) {
      clearTimeout(heroVideoDebounceRef.current);
    }

    heroVideoDebounceRef.current = setTimeout(async () => {
      setIsSearchingHeroVideo(true);
      try {
        const results = await searchVideos(heroVideoSearchQuery, { channels });
        setHeroVideoSearchResults(results);
      } catch (error) {
        console.error('Failed to search videos:', error);
        setHeroVideoSearchResults([]);
      } finally {
        setIsSearchingHeroVideo(false);
      }
    }, 300);

    return () => {
      if (heroVideoDebounceRef.current) {
        clearTimeout(heroVideoDebounceRef.current);
      }
    };
  }, [channels, heroVideoSearchQuery]);

  const videoUrl = heroVideo ? getVideoUrl(heroVideo) : '';

  // Setup HLS playback when video URL is HLS (m3u8) - native video can't play it in Chrome/Firefox
  useEffect(() => {
    if (!videoUrl || !heroVideo) {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      return;
    }
    const videoEl = videoRef.current;
    if (!videoEl) return;

    if (!isHlsUrl(videoUrl)) {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      return;
    }

    // Safari supports HLS natively
    if (videoEl.canPlayType('application/vnd.apple.mpegurl')) {
      videoEl.src = videoUrl;
      return () => {
        videoEl.removeAttribute('src');
        videoEl.load();
      };
    }

    // Use hls.js for Chrome, Firefox, etc.
    if (Hls.isSupported()) {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      const hls = new Hls();
      hlsRef.current = hls;
      hls.loadSource(videoUrl);
      hls.attachMedia(videoEl);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        if (videoEl && !isNaN(videoEl.duration)) {
          setActualVideoDuration(videoEl.duration);
        }
      });
      return () => {
        hls.destroy();
        hlsRef.current = null;
      };
    }
  }, [videoUrl]);

  // Clamp loop range when actual video duration loads (more accurate than API)
  useEffect(() => {
    if (!heroVideo || actualVideoDuration == null || actualVideoDuration <= 0) return;
    const maxD = actualVideoDuration;
    if (heroVideo.loopEnd > maxD || heroVideo.loopStart > maxD) {
      setHeroVideo((prev) =>
        prev
          ? {
              ...prev,
              loopStart: Math.min(prev.loopStart, maxD - 0.5),
              loopEnd: Math.min(prev.loopEnd, maxD),
            }
          : prev
      );
    }
  }, [actualVideoDuration, heroVideo]);

  /** Effective duration: from loaded video element first, then API/parsed string. */
  const getEffectiveDuration = useCallback((): number => {
    if (!heroVideo) return 60;
    if (actualVideoDuration !== null && !isNaN(actualVideoDuration) && actualVideoDuration > 0) {
      return actualVideoDuration;
    }
    return getVideoDuration(heroVideo);
  }, [heroVideo, actualVideoDuration]);

  const handleSelectHeroVideo = useCallback((video: VideoItem) => {
    setActualVideoDuration(null);
    const durationSeconds = getVideoDuration(video);
    setHeroVideo({
      ...video,
      loopStart: 0,
      loopEnd: Math.min(durationSeconds, 15), // Default to first 15 seconds or video length
    });
    setHeroVideoSearchQuery('');
    setHeroVideoSearchResults([]);
  }, []);

  const handleRemoveHeroVideo = useCallback(() => {
    setHeroVideo(null);
    setActualVideoDuration(null);
  }, []);

  const handleLoopRangeChange = useCallback(
    (_: Event, newValue: number | number[]) => {
      if (!heroVideo || !Array.isArray(newValue)) return;
      const [start, end] = newValue;
      const duration = getEffectiveDuration();
      const maxDuration = Math.max(duration, 1);
      const clampedStart = Math.max(0, Math.min(start, maxDuration - 0.5));
      const clampedEnd = Math.max(clampedStart + 0.5, Math.min(end, maxDuration));
      setHeroVideo((prev) =>
        prev ? { ...prev, loopStart: clampedStart, loopEnd: clampedEnd } : prev
      );
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.currentTime = clampedStart;
      }
      setIsPreviewPlaying(false);
    },
    [heroVideo, getEffectiveDuration]
  );

  // Handle video time update to loop within selected range
  const handleVideoTimeUpdate = useCallback(() => {
    if (videoRef.current && heroVideo && isPreviewPlaying) {
      const currentTime = videoRef.current.currentTime;
      if (currentTime >= heroVideo.loopEnd || currentTime < heroVideo.loopStart) {
        videoRef.current.currentTime = heroVideo.loopStart;
      }
    }
  }, [heroVideo, isPreviewPlaying]);

  const handleLoadedMetadata = useCallback(() => {
    if (videoRef.current && !isNaN(videoRef.current.duration)) {
      setActualVideoDuration(videoRef.current.duration);
    }
  }, []);

  const handlePlayPreview = useCallback(() => {
    if (videoRef.current && heroVideo) {
      videoRef.current.currentTime = heroVideo.loopStart;
      void videoRef.current.play().catch((err: DOMException) => {
        if (err.name !== 'AbortError') {
          console.error('Failed to play hero video preview:', err);
        }
      });
      setIsPreviewPlaying(true);
    }
  }, [heroVideo]);

  const handlePausePreview = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.pause();
      setIsPreviewPlaying(false);
    }
  }, []);

  return {
    heroVideo,
    heroVideoSearchQuery,
    heroVideoSearchResults,
    isSearchingHeroVideo,
    isPreviewPlaying,
    actualVideoDuration,
    videoRef,
    videoUrl,
    setHeroVideoSearchQuery,
    handleSelectHeroVideo,
    handleRemoveHeroVideo,
    handleLoopRangeChange,
    handleVideoTimeUpdate,
    handleLoadedMetadata,
    handlePlayPreview,
    handlePausePreview,
    getEffectiveDuration,
    setHeroVideo,
  };
}
